import { supabase } from './supabaseService';

export interface RecordingProviderSettings {
  id?: string;
  user_id: string;
  active_provider: 'fathom' | 'none';
  auto_record: boolean;
  default_quality: 'low' | 'medium' | 'high';
  retention_days: number;
  fathom_enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

class RecordingProviderService {
  async getProviderSettings(userId: string): Promise<RecordingProviderSettings | null> {
    try {
      console.log('📹 Getting recording provider settings for user:', userId);

      const { data, error } = await supabase
        .from('recording_provider_settings')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('❌ Error getting provider settings:', error);
        throw error;
      }

      if (!data) {
        console.log('ℹ️ No provider settings found, creating default settings');
        return await this.createDefaultSettings(userId);
      }

      console.log('✅ Provider settings loaded:', data);
      return data;
    } catch (error) {
      console.error('❌ Error in getProviderSettings:', error);
      throw error;
    }
  }

  async createDefaultSettings(userId: string): Promise<RecordingProviderSettings> {
    try {
      console.log('📹 Creating default provider settings for user:', userId);

      const defaultSettings: Omit<RecordingProviderSettings, 'id' | 'created_at' | 'updated_at'> = {
        user_id: userId,
        active_provider: 'none',
        auto_record: true,
        default_quality: 'medium',
        retention_days: 90,
        fathom_enabled: false
      };

      const { data, error } = await supabase
        .from('recording_provider_settings')
        .insert([defaultSettings])
        .select()
        .single();

      if (error) {
        console.error('❌ Error creating default settings:', error);
        throw error;
      }

      console.log('✅ Default provider settings created:', data);
      return data;
    } catch (error) {
      console.error('❌ Error in createDefaultSettings:', error);
      throw error;
    }
  }

  async updateProviderSettings(
    userId: string,
    updates: Partial<Omit<RecordingProviderSettings, 'id' | 'user_id' | 'created_at' | 'updated_at'>>
  ): Promise<RecordingProviderSettings> {
    try {
      console.log('📹 Updating provider settings for user:', userId, updates);

      const { data, error } = await supabase
        .from('recording_provider_settings')
        .update(updates)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating provider settings:', error);
        throw error;
      }

      console.log('✅ Provider settings updated:', data);
      return data;
    } catch (error) {
      console.error('❌ Error in updateProviderSettings:', error);
      throw error;
    }
  }

  async toggleFathom(userId: string, enabled: boolean): Promise<RecordingProviderSettings> {
    try {
      console.log(`📹 ${enabled ? 'Enabling' : 'Disabling'} Fathom for user:`, userId);

      const currentSettings = await this.getProviderSettings(userId);

      if (!currentSettings) {
        throw new Error('Provider settings not found');
      }

      const updates: Partial<RecordingProviderSettings> = {
        fathom_enabled: enabled,
        active_provider: enabled ? 'fathom' : 'none'
      };

      return await this.updateProviderSettings(userId, updates);
    } catch (error) {
      console.error('❌ Error in toggleFathom:', error);
      throw error;
    }
  }

  async isFathomEnabled(userId: string): Promise<boolean> {
    try {
      const settings = await this.getProviderSettings(userId);
      return settings?.fathom_enabled || false;
    } catch (error) {
      console.error('❌ Error checking if Fathom is enabled:', error);
      return false;
    }
  }

}

export const recordingProviderService = new RecordingProviderService();
